import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  formatDate,
  INDUSTRY_VOICES_THEME,
  SITE_URL,
} from '../../config';
import { useCategoryFromQuery, useThemeFromQuery } from '../../hooks/useQueryFilters';
import SlotReviewShowcase from '../slots/SlotReviewShowcase';
import IndustryVoicesWidget from './IndustryVoicesWidget';
import { prefetchImages } from '../../utils/prefetchImages';
import { LoadingBar, Spinner } from '../common/LoadingIndicators';

function normaliseToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function isIndustryVoices(post) {
  const want = normaliseToken(INDUSTRY_VOICES_THEME);
  const category = normaliseToken(post?.category);
  if (category === want) return true;
  const themes = Array.isArray(post?.themes) ? post.themes : [];
  return themes.some((t) => normaliseToken(t) === want);
}

function NewsFeed({ variant = 'home' }) {
  const activeCategory = useCategoryFromQuery();
  const activeTheme = useThemeFromQuery();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState('latest'); // 'latest' | 'popular'
  const [loadedImages, setLoadedImages] = useState(() => ({}));

  const markLoaded = (key) => {
    if (!key) return;
    setLoadedImages((prev) => {
      if (prev[key]) return prev;
      return { ...prev, [key]: true };
    });
  };

  useEffect(() => {
    const q = query(
      collection(db, 'posts'),
      orderBy('publishedAt', 'desc'),
      // We filter unpublished client-side to avoid needing a Firestore composite index.
      // Keep this limit comfortably above what we render so unpublished drafts don't
      // crowd out published posts.
      limit(250)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // Avoid rendering stale cached snapshots on refresh (causes brief fallback-image flash)
        // and wait for the first server snapshot.
        if (snapshot.metadata?.fromCache) return;
        const items = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPosts(items);
        setLoading(false);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('Error loading posts from Firestore', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const byCategory =
    activeCategory === 'All'
      ? posts
      : posts.filter((a) => a.category === activeCategory);

  // Hide drafts (published defaults to true when missing).
  const publishedOnly = byCategory.filter((a) => a && a.published !== false);

  const filtered = activeTheme
    ? publishedOnly.filter(
        (a) => Array.isArray(a.themes) && a.themes.includes(activeTheme)
      )
    : publishedOnly;

  // Keep "Industry Voices" out of the main news feed; it has its own block/page.
  const feedItems = filtered.filter((a) => !isIndustryVoices(a));

  const getTimeMs = (value) => {
    if (!value) return 0;
    if (typeof value === 'string') return new Date(value).getTime() || 0;
    if (value.toMillis) return value.toMillis();
    try {
      return new Date(value).getTime() || 0;
    } catch {
      return 0;
    }
  };

  const sorted = [...feedItems].sort((a, b) => {
    const dateA = getTimeMs(a.publishedAt);
    const dateB = getTimeMs(b.publishedAt);

    if (sortMode === 'latest') {
      return dateB - dateA;
    }

    const viewsA = typeof a.views === 'number' ? a.views : 0;
    const viewsB = typeof b.views === 'number' ? b.views : 0;
    if (viewsB !== viewsA) return viewsB - viewsA;
    return dateB - dateA;
  });

  const [featured, ...rest] = sorted;
  const isHome = variant === 'home';
  const latestTiles = isHome ? rest.slice(0, 6) : rest;

  useEffect(() => {
    // Prefetch first N card images to reduce "flash" on refresh / quick scroll.
    const urls = [];
    if (featured) {
      urls.push(featured.imageThumbUrl || featured.imageUrl);
    }
    for (const a of latestTiles) {
      urls.push(a.imageThumbUrl || a.imageUrl);
    }
    // Home: featured + 6 tiles => 7; All: prefetch a bit more.
    prefetchImages(urls, { max: isHome ? 10 : 14 });
  }, [featured, isHome, latestTiles]);

  const canonicalUrl = `${SITE_URL}${location.pathname}${location.search || ''}`;

  return (
    <section aria-label="Latest iGaming news">
      <Helmet>
        <title>IGAMINGSPOT – Regulated Casino &amp; Betting Data &amp; News</title>
        <meta
          name="description"
          content="Daily, government-controlled iGaming news and official statistics for regulated casino, sports betting, tax &amp; compliance, and market performance."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="IGAMINGSPOT – Regulated Casino &amp; Betting Data &amp; News" />
        <meta
          property="og:description"
          content="White-market iGaming insights, market stats, regulatory updates, and sportsbook performance data for licenced operators."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SITE_URL}/logo512.png`} />
        <meta property="og:site_name" content="IGAMINGSPOT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="IGAMINGSPOT – Regulated Casino &amp; Betting Data &amp; News" />
        <meta
          name="twitter:description"
          content="Government-compliant coverage of regulated casino and betting markets worldwide."
        />
        <meta name="twitter:image" content={`${SITE_URL}/logo512.png`} />
      </Helmet>
      <div className="content-card news-feed-card">
        {loading && <LoadingBar />}
        <div className="content-card-header">
          <div>
            <div className="section-title">Regulated news feed</div>
            <h1 className="section-subtitle">Government-controlled iGaming headlines</h1>
          </div>
          <div className="tabs" aria-label="Sort options">
            <button
              type="button"
              className={`tab-pill ${sortMode === 'latest' ? 'active' : ''}`}
              onClick={() => setSortMode('latest')}
            >
              Latest
            </button>
            <button
              type="button"
              className={`tab-pill ${sortMode === 'popular' ? 'active' : ''}`}
              onClick={() => setSortMode('popular')}
            >
              Most read
            </button>
          </div>
        </div>

        {loading && !posts.length && (
          <p className="secondary-text" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Spinner /> Loading regulated news from the database…
          </p>
        )}

        {!loading && !posts.length && (
          <p className="secondary-text" style={{ marginTop: 4 }}>
            No regulated posts are available yet. Use the AI post writer to add your first article.
          </p>
        )}

        {activeTheme && (
          <p className="secondary-text" style={{ marginTop: 4 }}>
            Filtered by theme: <strong>{activeTheme}</strong>
          </p>
        )}

        {featured && (
          <Link to={`/news/${featured.slug}`} itemProp="url" className="featured-link">
            <article
              className="featured-article"
              itemScope
              itemType="https://schema.org/NewsArticle"
            >
              <div className="featured-media">
                {(() => {
                  const imgSrc = featured.imageThumbUrl || featured.imageUrl;
                  return (
                    imgSrc && (
                      <img
                        key={imgSrc}
                        src={imgSrc}
                        alt={featured.title}
                        className="featured-image"
                        loading="eager"
                        referrerPolicy="no-referrer"
                        fetchPriority="high"
                        decoding="async"
                        onLoad={() => markLoaded(`featured:${imgSrc}`)}
                        onError={(e) => {
                          const target = e.target;
                          // eslint-disable-next-line no-param-reassign
                          target.style.display = 'none';
                        }}
                      />
                    )
                  );
                })()}
                <div className="featured-media-inner">
                  <div className="featured-media-meta">
                    <span className="badge-pill">{featured.region}</span>
                    {featured.kpi && (
                      <span className="kpi-chip">
                        <strong>{featured.kpi}</strong>
                      </span>
                    )}
                  </div>
                  <div className="category-tag" itemProp="articleSection">
                    {featured.category}
                  </div>
                </div>
              </div>
              <div className="featured-content">
                <div className="news-footer featured-meta">
                  <span>
                    Published{' '}
                    <time dateTime={String(featured.publishedAt)} itemProp="datePublished">
                      {formatDate(featured.publishedAt)}
                    </time>
                  </span>
                  {featured.readMinutes && (
                    <>
                      <span className="dot" aria-hidden="true" />
                      <span>{featured.readMinutes} min read</span>
                    </>
                  )}
                  <span className="dot" aria-hidden="true" />
                  <span>White-market only · verified data</span>
                </div>
                <h2 className="featured-title" itemProp="headline">
                  {featured.title}
                </h2>
                <p className="featured-excerpt" itemProp="description">
                  {featured.excerpt}
                </p>
              </div>
            </article>
          </Link>
        )}

        {rest.length > 0 && (
          <div className="latest-block">
            <div className="latest-header">
              <h2 className="latest-title">Latest news</h2>
              {isHome && (
                <Link to={`/news${location.search || ''}`} className="latest-see-all">
                  See all →
                </Link>
              )}
            </div>
            <div className="news-grid">
              {latestTiles.map((article) => {
                const slug = article.slug;

                return (
                  <Link key={slug} to={`/news/${slug}`} itemProp="url">
                    <article
                      className="news-tile"
                      itemScope
                      itemType="https://schema.org/NewsArticle"
                    >
                      {(() => {
                        const imgSrc = article.imageThumbUrl || article.imageUrl;
                        return (
                          imgSrc && (
                            <div
                              className="news-tile-image-wrap"
                              data-loaded={loadedImages[`tile:${imgSrc}`] ? 'true' : 'false'}
                              style={{
                                '--lqip-bg': article.imageLqip ? `url("${article.imageLqip}")` : undefined,
                              }}
                            >
                              <img
                                key={imgSrc}
                                src={imgSrc}
                                alt={article.title}
                                className="news-tile-image"
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                decoding="async"
                                fetchPriority="auto"
                                onLoad={() => markLoaded(`tile:${imgSrc}`)}
                                onError={(e) => {
                                  const target = e.target;
                                  // eslint-disable-next-line no-param-reassign
                                  target.style.display = 'none';
                                }}
                              />
                            </div>
                          )
                        );
                      })()}
                      <div className="news-tile-meta">
                        <span className="badge-pill">{article.region}</span>
                        <span className="dot" aria-hidden="true" />
                        <span className="secondary-text">
                          <time
                            dateTime={String(article.publishedAt)}
                            itemProp="datePublished"
                          >
                            {formatDate(article.publishedAt)}
                          </time>
                        </span>
                      </div>
                      <h3 className="news-tile-title" itemProp="headline">
                        {article.title}
                      </h3>
                      <p className="news-tile-excerpt" itemProp="description">
                        {article.excerpt}
                      </p>
                      <div className="news-tile-footer">
                        <span className="secondary-text">{article.category}</span>
                        {article.readMinutes && (
                          <>
                            <span className="dot" aria-hidden="true" />
                            <span className="secondary-text">
                              {article.readMinutes} min read
                            </span>
                          </>
                        )}
                      </div>
                    </article>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {isHome && <SlotReviewShowcase />}
      {isHome && <IndustryVoicesWidget />}
    </section>
  );
}

export default NewsFeed;


